import React, { useEffect, useState } from 'react';
import { MessagesSquare, Plus, X, CheckSquare, Square, AlertCircle, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut } from '../../lib/api';

const ACTIVITY_TYPES = ['Client Meeting', 'Techno-Commercial Discussion', 'Technical Discussion', 'Site Visit Meeting', 'Call', 'Email', 'Other'];
const TYPE_CHIP = {
  'Client Meeting': 'bg-blue-50 text-blue-800 border-blue-200',
  'Techno-Commercial Discussion': 'bg-violet-50 text-violet-800 border-violet-200',
  'Technical Discussion': 'bg-sky-50 text-sky-800 border-sky-200',
  'Site Visit Meeting': 'bg-amber-50 text-amber-800 border-amber-200',
  'Call': 'bg-emerald-50 text-emerald-800 border-emerald-200',
  'Email': 'bg-slate-100 text-slate-700 border-slate-200',
  'Other': 'bg-slate-50 text-slate-600 border-slate-200',
};
const lbl = 'block text-[11px] font-display font-bold uppercase tracking-wider text-slate-500';
const inp = 'mt-1 w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-[#3b4872] outline-none';
const today = new Date().toISOString().slice(0, 10);

const blank = () => ({ activity_type: 'Client Meeting', date: today, attendees: '', minutes: '', action_items: [{ text: '', owner: '', due_date: '', done: false }] });

export default function EnquiryActivityTab({ enqId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await apiGet(`/enquiries/${enqId}/activities`); setItems(r.items || []); }
    catch { toast.error('Could not load activity log'); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (enqId) load(); }, [enqId]);

  const openNew = () => { setEditingId(null); setForm(blank()); setShowForm(true); };
  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      activity_type: a.activity_type, date: (a.date || '').slice(0, 10),
      attendees: (a.attendees || []).join(', '), minutes: a.minutes || '',
      action_items: (a.action_items || []).length ? a.action_items.map((x) => ({ ...x, due_date: (x.due_date || '').slice(0, 10) })) : [{ text: '', owner: '', due_date: '', done: false }],
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.date) { toast.error('Pick a date'); return; }
    setSaving(true);
    const payload = {
      activity_type: form.activity_type,
      date: form.date,
      attendees: form.attendees.split(',').map((s) => s.trim()).filter(Boolean),
      minutes: form.minutes,
      action_items: form.action_items.filter((a) => a.text.trim()).map((a) => ({ text: a.text, owner: a.owner, due_date: a.due_date || null, done: !!a.done })),
    };
    try {
      if (editingId) await apiPut(`/enquiries/${enqId}/activities/${editingId}`, payload);
      else await apiPost(`/enquiries/${enqId}/activities`, payload);
      toast.success(editingId ? 'Activity updated' : 'Activity logged');
      setShowForm(false); load();
    } catch { toast.error('Could not save activity'); }
    finally { setSaving(false); }
  };

  const setAi = (i, k, v) => setForm((s) => ({ ...s, action_items: s.action_items.map((a, idx) => idx === i ? { ...a, [k]: v } : a) }));

  return (
    <div className="space-y-4" data-testid="enq-activity-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessagesSquare size={16} className="text-[#3b4872]" />
          <h3 className="font-display font-bold uppercase tracking-wide text-sm">Activity Log</h3>
          <span className="text-[11px] text-slate-500">{items.length} entries</span>
        </div>
        <button onClick={openNew} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856] font-display font-bold uppercase tracking-wider" data-testid="enq-act-add-btn">
          <Plus size={13} /> Log Activity
        </button>
      </div>

      {loading ? (
        <p className="text-xs text-slate-400">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid="enq-act-empty">No activities logged. Record client meetings and discussions here.</p>
      ) : (
        <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4" data-testid="enq-act-timeline">
          {items.map((a) => (
            <li key={a.id} className="ml-4" data-testid={`enq-act-${a.id}`}>
              <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-[#3b4872]" />
              <div className="bg-white border border-slate-200 rounded-md p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`tag border ${TYPE_CHIP[a.activity_type] || ''}`}>{a.activity_type}</span>
                    <span className="text-[11px] text-slate-500">{(a.date || '').slice(0, 10)}</span>
                  </div>
                  <button onClick={() => openEdit(a)} className="text-slate-400 hover:text-[#3b4872]" data-testid={`enq-act-edit-${a.id}`}><Pencil size={13} /></button>
                </div>
                {(a.attendees || []).length > 0 && <p className="text-[11px] text-slate-500 mt-1">Attendees: {a.attendees.join(', ')}</p>}
                {a.minutes ? <p className="text-sm mt-1.5 whitespace-pre-wrap">{a.minutes}</p> : null}
                {(a.action_items || []).length > 0 && (
                  <div className="mt-2 space-y-1">
                    {a.action_items.map((ai, idx) => {
                      const overdue = !ai.done && ai.due_date && (ai.due_date.slice(0, 10) < today);
                      return (
                        <div key={ai.id || idx} className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${overdue ? 'bg-rose-50 border border-rose-200' : 'bg-slate-50'}`}>
                          {ai.done ? <CheckSquare size={12} className="text-emerald-600" /> : <Square size={12} className="text-slate-400" />}
                          <span className={ai.done ? 'line-through text-slate-400' : ''}>{ai.text}</span>
                          {ai.owner ? <span className="text-slate-400">· {ai.owner}</span> : null}
                          {ai.due_date ? <span className={`ml-auto ${overdue ? 'text-rose-600 font-bold flex items-center gap-1' : 'text-slate-400'}`}>{overdue && <AlertCircle size={10} />}{ai.due_date.slice(0, 10)}</span> : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      {showForm && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowForm(false)} />
          <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col" data-testid="enq-act-form">
            <header className="px-5 py-4 border-b flex items-center justify-between bg-[#3b4872] text-white">
              <h2 className="font-display font-bold tracking-wide uppercase text-sm">{editingId ? 'Edit Activity' : 'Log Activity'}</h2>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </header>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><label className={lbl}>Type *</label>
                  <select value={form.activity_type} onChange={(e) => setForm((s) => ({ ...s, activity_type: e.target.value }))} className={inp} data-testid="enq-act-type">
                    {ACTIVITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div><label className={lbl}>Date *</label><input type="date" value={form.date} onChange={(e) => setForm((s) => ({ ...s, date: e.target.value }))} className={inp} data-testid="enq-act-date" /></div>
              </div>
              <div><label className={lbl}>Attendees (comma separated)</label><input value={form.attendees} onChange={(e) => setForm((s) => ({ ...s, attendees: e.target.value }))} className={inp} data-testid="enq-act-attendees" /></div>
              <div><label className={lbl}>Minutes</label><textarea value={form.minutes} onChange={(e) => setForm((s) => ({ ...s, minutes: e.target.value }))} rows={4} className={inp} data-testid="enq-act-minutes" /></div>
              <div>
                <div className="flex items-center justify-between">
                  <label className={lbl}>Action Items</label>
                  <button onClick={() => setForm((s) => ({ ...s, action_items: [...s.action_items, { text: '', owner: '', due_date: '', done: false }] }))} className="text-[11px] text-[#3b4872] font-bold" data-testid="enq-act-add-action">+ Add</button>
                </div>
                <div className="space-y-2 mt-1">
                  {form.action_items.map((ai, i) => (
                    <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                      <input value={ai.text} onChange={(e) => setAi(i, 'text', e.target.value)} placeholder="Action" className="col-span-5 border border-slate-300 rounded px-2 py-1 text-xs" data-testid={`enq-act-ai-text-${i}`} />
                      <input value={ai.owner} onChange={(e) => setAi(i, 'owner', e.target.value)} placeholder="Owner" className="col-span-3 border border-slate-300 rounded px-2 py-1 text-xs" />
                      <input type="date" value={ai.due_date || ''} onChange={(e) => setAi(i, 'due_date', e.target.value)} className="col-span-3 border border-slate-300 rounded px-1 py-1 text-xs" />
                      <input type="checkbox" checked={!!ai.done} onChange={(e) => setAi(i, 'done', e.target.checked)} className="col-span-1" title="Done" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <footer className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
              <button onClick={submit} disabled={saving} className="px-4 py-1.5 bg-[#3b4872] text-white text-xs font-display font-bold uppercase tracking-wide rounded-md disabled:opacity-50" data-testid="enq-act-submit">{saving ? 'Saving…' : 'Save'}</button>
            </footer>
          </div>
        </>
      )}
    </div>
  );
}
